import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import type {
  SurveyDesignReadinessDto, SurveyQuotaCellDto, SurveyQuotaDimensionDto, SurveyResponseDto,
  SurveyStratumDto, SurveyVersionResponseDto,
} from '@/shared/api/types'
import {
  deleteStratum, getDesignReadiness, getInstrument, getPoll, getPopulationFrame, getQuotaStatus,
  getRegisterVocabulary, listPolls, listStrata,
} from './api'
import { PollSelect } from './PollSelect'
import { PopulationFrameEditor } from './PopulationFrameEditor'
import { QuotaDimensionDialog } from './QuotaDimensionDialog'
import { StratumDialog } from './StratumDialog'
import { Pill, count, num, pct, weightMethodLabel, selectionLabel } from './vocab'

/**
 * Whom are we asking, how many, and against what population.
 *
 * ## This screen is why the Results screen can ever show a number
 *
 * Every estimate this product publishes is suppressed today, and the chain is
 * short: an estimate is suppressed unless it comes from a weight run, a weight
 * run that projects to a population needs a population frame, and until this
 * screen existed nothing in the application could enter one. Strata, quotas and
 * the frame were read-only — visible, unfixable. So the readiness panel is
 * first on the page and says so in as many words.
 *
 * ## Why the readiness answer belongs here and not on Results
 *
 * "This poll cannot be weighted" is worth knowing while there is still time to
 * fix it. On the Results screen it arrives as a suppressed estimate on the day
 * somebody needed the number, and by then the fieldwork is done.
 *
 * ## What the client does and does not compute
 *
 * Blockers are server-composed sentences, rendered as a list, never assembled
 * here from booleans — methodology written in a component is methodology
 * nobody can review. Two ratios ARE computed on this page: the implied design
 * weight per stratum and the frame shares. Both are defended where they are
 * written, and neither has a server-computed counterpart it could disagree with.
 */
export function SamplePage() {
  const canRead = useCan('survey:read')
  const canUpdate = useCan('survey:update')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')

  const [stratumEdit, setStratumEdit] = useState<{ row: SurveyStratumDto | null } | null>(null)
  const [quotaEdit, setQuotaEdit] = useState<{ row: SurveyQuotaDimensionDto | null } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [writeError, setWriteError] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  // Read by id rather than found in the poll list: the list is the picker's, one
  // page of it, and a poll outside that page is a poll this screen would other-
  // wise know nothing about — no version to read the instrument from, no target
  // sample to resolve a SHARE cell against.
  const poll = useLoad(() => (pollId ? getPoll(pollId) : Promise.resolve(null)), [pollId])
  const readiness = useLoad(
    () => (pollId ? getDesignReadiness(pollId) : Promise.resolve(null)), [pollId])
  const strata = useLoad(() => (pollId ? listStrata(pollId) : Promise.resolve([])), [pollId])
  const quota = useLoad(
    () => (pollId ? getQuotaStatus(pollId) : Promise.resolve(null)), [pollId])
  const frame = useLoad(
    () => (pollId ? getPopulationFrame(pollId) : Promise.resolve([])), [pollId])
  const register = useLoad(() => getRegisterVocabulary(), [])

  /**
   * `SurveyResponse` carries the whole `currentVersion` object, not an id.
   *
   * Read through a local shape because `SurveyResponseDto` still declares an
   * invented `currentVersionId` that the server has never sent: reading the
   * declared field compiles, returns `undefined` in production, and leaves the
   * quota editor with no questions to source a dimension from and nothing said
   * about why. Delete this cast when types.ts is corrected.
   */
  const pollData = poll.data as (SurveyResponseDto & {
    currentVersion: SurveyVersionResponseDto | null
  }) | null
  const versionId = pollData?.currentVersion?.id ?? null
  /** Only for a QUESTION-sourced quota dimension's value codes. A poll with no published version has none. */
  const instrument = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])

  /**
   * What a SHARE cell resolves against, or nothing.
   *
   * Never `?? 0`: a zero here is a target nobody set, and the quota editor states
   * it in two places — "this poll's target sample of 0", and "Resolves to 0" on
   * every SHARE cell. The poll's own target is the fallback for a failed quota
   * read; when neither is loaded the editor is not opened on a made-up figure.
   */
  const targetSample = quota.data?.targetSample ?? pollData?.targetSample ?? null

  if (!canRead) return <Forbidden what="the sample design" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
    setWriteError(null)
  }

  /**
   * Every write ends here.
   *
   * Readiness is reloaded on all of them, not just the ones that obviously touch
   * it, because that is the point of the panel: adding the last frame row should
   * make the blocker disappear in front of the person who added it, and a stale
   * blocker list is worse than none.
   */
  const afterWrite = (what: 'strata' | 'quota' | 'frame') => {
    readiness.reload()
    if (what === 'strata') strata.reload()
    if (what === 'quota') quota.reload()
    if (what === 'frame') frame.reload()
  }

  /**
   * The editor opens on an UNKNOWN target and says so, rather than on `?? 0`.
   *
   * A zero is indistinguishable inside the editor from a target somebody set: it
   * prints as this poll's own and every SHARE cell resolves against it, so the
   * screen states a target nobody chose with the same confidence as one they
   * did. Null instead — the dialog takes `number | null` and prints an em dash.
   *
   * Refusing to open would be the safer-looking choice and the worse one: a
   * COUNT cell does not need the target at all, so it would block work that has
   * nothing to do with the missing number.
   */
  const openQuota = (row: SurveyQuotaDimensionDto | null) => {
    setWriteError(null)
    setQuotaEdit({ row })
  }

  async function removeStratum(row: SurveyStratumDto) {
    if (!pollId) return
    setWriteError(null)
    try {
      await deleteStratum(pollId, row.id)
      setConfirmDelete(null)
      afterWrite('strata')
    } catch (e) {
      // `survey/stratum-in-use`: a stratum holding interviews is not a row, it is
      // the explanation of how those interviews were selected. The server's
      // sentence says so and names the alternative (set the target to 0).
      setWriteError(explain(e))
      setConfirmDelete(null)
    }
  }

  const strataRows = strata.data ?? []
  const dimensions = quota.data?.dimensions ?? []
  const frameRows = frame.data ?? []
  const cellCount = dimensions.reduce((n, d) => n + d.cells.length, 0)
  const loadingFirst = !readiness.data && !quota.data && strataRows.length === 0
    && (readiness.loading || strata.loading || quota.loading || frame.loading)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Sample"
        description="Strata, quotas and the population frame — the three things a weight run needs, and the only place they can be entered."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      <ErrorStrip message={writeError ?? poll.error ?? readiness.error ?? strata.error
        ?? quota.error ?? frame.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll to see whether it can be weighted.</Note>
      ) : loadingFirst ? <LoadingBlock label="Loading the design…" /> : (
        <>
          <ReadinessPanel readiness={readiness.data} />

          <KpiStrip>
            <MetricCard label="Strata" value={count(strataRows.length)}
              sub={strataRows.length === 0 ? 'unstratified' : 'declared'} />
            <MetricCard label="Quota cells" value={count(cellCount)}
              sub={`across ${dimensions.length} dimension${dimensions.length === 1 ? '' : 's'}`} />
            <MetricCard label="Frame rows" value={count(frameRows.length)}
              sub={`${new Set(frameRows.map((r) => r.dimensionCode)).size} dimension(s) to rake to`}
              accent={frameRows.length === 0} />
            <MetricCard label="Enforcement" value={quota.data?.enforcement ?? '—'}
              sub="what happens when a cell fills" />
            <MetricCard label="Collected"
              value={quota.data === null ? '—' : count(quota.data.collected)}
              sub={quota.data === null ? 'not loaded'
                : `of ${count(quota.data.targetSample)} target`} />
          </KpiStrip>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 xl:col-span-7">
              <StrataPanel
                rows={strataRows}
                canUpdate={canUpdate}
                confirmDelete={confirmDelete}
                onConfirmDelete={setConfirmDelete}
                onAdd={() => setStratumEdit({ row: null })}
                onEdit={(row) => setStratumEdit({ row })}
                onDelete={removeStratum}
              />
            </div>

            <div className="col-span-12 xl:col-span-5 space-y-4">
              <QuotasPanel
                dimensions={dimensions}
                enforcement={quota.data?.enforcement ?? null}
                canUpdate={canUpdate}
                onAdd={() => openQuota(null)}
                onEdit={(row) => openQuota(row)}
              />
            </div>
          </div>

          <PopulationFrameEditor
            surveyId={pollId}
            rows={frameRows}
            dimensions={dimensions}
            canUpdate={canUpdate}
            onSaved={() => afterWrite('frame')}
          />

          {stratumEdit && (
            <StratumDialog
              open
              surveyId={pollId}
              stratum={stratumEdit.row}
              onClose={() => setStratumEdit(null)}
              onSaved={() => { setStratumEdit(null); afterWrite('strata') }}
            />
          )}

          {quotaEdit && (
            <QuotaDimensionDialog
              open
              surveyId={pollId}
              dimension={quotaEdit.row}
              targetSample={targetSample}
              register={register.data}
              questions={instrument.data?.questions ?? []}
              onClose={() => setQuotaEdit(null)}
              onSaved={() => { setQuotaEdit(null); afterWrite('quota') }}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * Can this poll be weighted, and if not, what is in the way.
 *
 * The blockers are printed exactly as the server composed them. It knows which
 * checks it actually ran and in what order; a client that turned
 * `{strataDeclared: false, populationFrameDeclared: false}` back into prose
 * would be inventing methodology in a component, and would drift the first time
 * the server added a check.
 */
function ReadinessPanel({ readiness }: { readiness: SurveyDesignReadinessDto | null }) {
  if (!readiness) return null
  const blocked = readiness.blockers.length > 0

  return (
    <Panel
      title="Can this poll be weighted?"
      description="Asked here, while there is still time to answer it — not on results day."
    >
      {blocked ? (
        <div className="space-y-3">
          <Note tone="bad">
            Not yet. Until this list is empty a weight run can only produce design weights of 1,
            so every estimate it makes is stamped NOT_WEIGHTED and suppressed — the Results
            screen shows the suppression sentence where the number should be. This screen is the
            only place in the product where these can be entered.
          </Note>
          <ol className="space-y-2">
            {readiness.blockers.map((sentence, i) => (
              // Index key: the list is server-ordered, replaced whole on every
              // reload, and two blockers can legitimately read the same.
              <li key={i} className="flex gap-2.5 text-erp-sm">
                <span className="shrink-0 h-5 w-5 rounded bg-muted text-muted-foreground
                  text-erp-xs inline-flex items-center justify-center tabular-nums">
                  {i + 1}
                </span>
                <span className="min-w-0">{sentence}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <Note tone="good">
          Yes. {expectedMethod(readiness)} Every estimate will carry that method, its design
          effect and its effective n, measured from the weights the run actually produced.
        </Note>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-erp-xs">
        <Pill>{`${count(readiness.strataCount)} ${readiness.strataCount === 1 ? 'stratum' : 'strata'}`}</Pill>
        <Pill>{`${count(readiness.quotaCellCount)} quota cell${readiness.quotaCellCount === 1 ? '' : 's'}`}</Pill>
        {readiness.frameDimensions.length === 0
          ? <Pill tone="bg-amber-500/10 text-amber-600 border-amber-500/30">no frame</Pill>
          : readiness.frameDimensions.map((d) => (
            <Pill key={d}>{`frame · ${d}`}</Pill>
          ))}
      </div>
    </Panel>
  )
}

/**
 * What method a clean design will get, in one sentence.
 *
 * Inferred on the client, which is a deliberate exception rather than an
 * oversight: `DesignReadinessResponse` carries no method, and the brief for this
 * panel is that an empty blocker list should say what it bought you. The
 * inference follows the same shape the weighting service does — margins to rake
 * on, or one margin to post-stratify on, or neither — and is worded as an
 * expectation, because the authoritative method is stamped on the run and shown
 * on Results. If the two ever disagree, Results is right.
 */
function expectedMethod(r: SurveyDesignReadinessDto): string {
  const method = r.frameDimensions.length > 1 ? 'RAKING'
    : r.frameDimensions.length === 1 ? 'POST_STRATIFICATION'
      : 'DESIGN_ONLY'
  const label = weightMethodLabel(method)
  if (method === 'RAKING') {
    return `Expect ${label.toLowerCase()} to ${r.frameDimensions.length} margins `
      + `(${r.frameDimensions.join(', ')}), on top of the design weights the ${r.strataCount} `
      + 'strata provide.'
  }
  if (method === 'POST_STRATIFICATION') {
    return `Expect post-stratification to one margin (${r.frameDimensions[0]}). A second frame `
      + 'dimension would get it raking, which corrects two margins at once instead of one.'
  }
  return `Expect ${label.toLowerCase()} from the ${r.strataCount} declared strata — projecting `
    + 'to the strata, not to a population.'
}

function StrataPanel({
  rows, canUpdate, confirmDelete, onConfirmDelete, onAdd, onEdit, onDelete,
}: {
  rows: SurveyStratumDto[]
  canUpdate: boolean
  confirmDelete: string | null
  onConfirmDelete: (id: string | null) => void
  onAdd: () => void
  onEdit: (row: SurveyStratumDto) => void
  onDelete: (row: SurveyStratumDto) => void
}) {
  return (
    <Panel
      title="Strata"
      description="A design weight is N_h / n_h. A stratum with no population size has no numerator."
      right={canUpdate && (
        <button type="button" className="erp-btn h-8 text-erp-xs" onClick={onAdd}>
          <Plus className="h-3 w-3" /> Add stratum
        </button>
      )}
    >
      <ErpTable head={(
        <>
          <Th>Code</Th>
          <Th>Name</Th>
          <Th>Where</Th>
          <Th className="text-right">Population</Th>
          <Th className="text-right">Target n</Th>
          <Th>Selection</Th>
          <Th className="text-right">Achieved</Th>
          <Th className="text-right"
            title="Population divided by achieved — what a design-weight-only run would give everybody in this stratum, before trimming">
            Implied design weight
          </Th>
          <Th />
        </>
      )}>
        {rows.length === 0 ? (
          <EmptyRow colSpan={9} message={
            'No strata. The sample is whoever the interviewers reach, and the design weight for '
            + 'everybody is 1.'
          } />
        ) : rows.map((s) => (
          <tr key={s.id} className="hover:bg-muted/40">
            <Td className="font-mono text-erp-xs whitespace-nowrap">{s.code}</Td>
            <Td className="whitespace-nowrap">{s.name}</Td>
            <Td className="text-muted-foreground">{s.unitName ?? '—'}</Td>
            <Td className="text-right tabular-nums text-muted-foreground">
              {s.populationSize === null ? '—' : count(s.populationSize)}
            </Td>
            <Td className="text-right tabular-nums">{count(s.targetN)}</Td>
            <Td className="text-erp-xs text-muted-foreground">{selectionLabel(s.selectionCode)}</Td>
            <Td className="text-right tabular-nums">{count(s.achieved)}</Td>
            <Td className="text-right tabular-nums text-muted-foreground">
              {impliedDesignWeight(s) === null ? '—' : num(impliedDesignWeight(s))}
            </Td>
            <Td className="text-right whitespace-nowrap">
              {canUpdate && (confirmDelete === s.id ? (
                <span className="inline-flex items-center gap-1.5 text-erp-xs">
                  <span className="text-muted-foreground">Delete {s.code}?</span>
                  <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs text-red-700"
                    onClick={() => onDelete(s)}>Yes</button>
                  <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs"
                    onClick={() => onConfirmDelete(null)}>No</button>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                    aria-label={`Edit stratum ${s.code}`} onClick={() => onEdit(s)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {/*
                    Offered even where it will be refused. A stratum holding
                    interviews comes back 409 with a sentence naming the count and
                    the alternative; hiding the button would leave somebody
                    hunting for a delete that does not exist.
                  */}
                  <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                    aria-label={`Delete stratum ${s.code}`}
                    onClick={() => onConfirmDelete(s.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </Td>
          </tr>
        ))}
      </ErpTable>

      <p className="pt-2 text-erp-xs text-muted-foreground">
        The implied weight is population ÷ achieved, shown only where both exist. It is the
        arithmetic the design-weight step starts from, not the weight the run ships: raking,
        post-stratification and trimming all move it, and the run's own figure is on Results.
        Deleting a stratum that already holds interviews is refused — set its target to 0 instead,
        so the design stays readable and no interview is orphaned.
      </p>
    </Panel>
  )
}

/**
 * `N_h / n_h`, computed here, which is a judgement call against rule 2.
 *
 * Rule 2 forbids the client computing a percentage, and the reason is that a
 * client-computed figure can disagree with the server-computed one printed
 * beside it — which is exactly what PollSphere did. This ratio has no server
 * counterpart anywhere in the contract: `StratumResponse` carries the two
 * integers and no weight, and the weight run does not expose per-stratum
 * factors. So there is nothing for it to disagree with.
 *
 * It is shown because the two columns it comes from are unreadable as a pair.
 * 412,000 over 96 is not obviously "each of these interviews stands for 4,292
 * people", and that number is the whole reason a stratum with a big population
 * and thin fieldwork is dangerous. It is labelled "implied design weight" and
 * captioned as pre-trimming so it cannot be read as the run's output.
 *
 * `achieved > 0` is required, not defended against: 412,000 / 0 is Infinity, and
 * an infinite weight rendered as a number would be a claim that one interview
 * speaks for everybody.
 */
function impliedDesignWeight(s: SurveyStratumDto): number | null {
  if (s.populationSize === null || s.achieved <= 0) return null
  return s.populationSize / s.achieved
}

function QuotasPanel({ dimensions, enforcement, canUpdate, onAdd, onEdit }: {
  dimensions: SurveyQuotaDimensionDto[]
  enforcement: string | null
  canUpdate: boolean
  onAdd: () => void
  onEdit: (row: SurveyQuotaDimensionDto) => void
}) {
  if (dimensions.length === 0) {
    return (
      <Panel
        title="Quotas"
        right={canUpdate && (
          <button type="button" className="erp-btn h-8 text-erp-xs" onClick={onAdd}>
            <Plus className="h-3 w-3" /> Add dimension
          </button>
        )}
      >
        <Note tone="info">
          No quotas declared. Interviewers will reach whoever answers, and the sample's
          composition is whatever that turns out to be — which is fine only if the frame can
          rake it back into shape afterwards.
        </Note>
      </Panel>
    )
  }

  return (
    <>
      {dimensions.map((dimension, i) => (
        <Panel
          key={dimension.id}
          title={dimension.name || dimension.code}
          description={dimension.sourceCode === 'QUESTION'
            ? `From the answer to ${dimension.questionCode ?? 'a question'} — reported, not enforced at the door.`
            : dimension.sourceCode === 'GEOGRAPHY'
              ? 'From where the interview happened.'
              : "From the register's own vocabulary, so there is one list and not two."}
          right={canUpdate && (
            <div className="flex items-center gap-1.5">
              <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs text-chocolate"
                onClick={() => onEdit(dimension)}>
                Edit
              </button>
              {/* One "add" affordance, on the first panel, so it is findable
                  without repeating it under every dimension. */}
              {i === 0 && (
                <button type="button" className="erp-btn h-7 text-erp-xs" onClick={onAdd}>
                  <Plus className="h-3 w-3" /> Add
                </button>
              )}
            </div>
          )}
        >
          <ErpTable head={(
            <>
              <Th>Cell</Th>
              <Th>Basis</Th>
              <Th className="text-right">Target</Th>
              <Th>Fill</Th>
              <Th>State</Th>
            </>
          )}>
            {dimension.cells.length === 0 ? (
              <EmptyRow colSpan={5}
                message="No cells. This dimension enforces nothing and cannot be raked to." />
            ) : dimension.cells.map((cell) => (
              <QuotaCellRow key={cell.id} cell={cell} />
            ))}
          </ErpTable>
        </Panel>
      ))}

      <Note tone="info">
        Every cell states whether its target is a COUNT of people or a SHARE of the sample, and
        the editor refuses to guess. PollSphere's blob stores "30" with no way to say which, so
        nothing downstream can read it — a target of 30 that does not say whether it means thirty
        people or thirty per cent is unreadable by construction.
        {enforcement === 'BLOCK'
          ? ' Enforcement is BLOCK: the capture screen refuses an interview into a closed cell.'
          : enforcement === 'WARN'
            ? ' Enforcement is WARN: the capture screen warns the interviewer about a closed cell and accepts the interview anyway.'
            : enforcement === 'OFF'
              ? ' Enforcement is OFF: a closed cell is bookkeeping and stops nothing at the door.'
              : ''}
      </Note>
    </>
  )
}

function QuotaCellRow({ cell }: { cell: SurveyQuotaCellDto }) {
  /**
   * The bar is two counts drawn to scale, and no percentage is printed.
   *
   * Rule 2's concern is a stated figure the server did not state. A bar has no
   * figure in it: the text beside it is `achieved / target`, both integers off
   * the wire, and the geometry is how far between them fieldwork has got. A
   * "78%" here would be a client-computed number sitting next to server-computed
   * ones, which is the thing rule 2 is about.
   */
  const fill = cell.target !== null && cell.target > 0
    ? Math.min(1, cell.achieved / cell.target) : null

  return (
    <tr className="hover:bg-muted/40">
      <Td>
        {cell.label ?? cell.valueCode}
        <span className="ml-1.5 font-mono text-erp-xs text-muted-foreground">{cell.valueCode}</span>
      </Td>
      <Td className="text-erp-xs text-muted-foreground whitespace-nowrap">
        {cell.basisCode}
        {cell.basisCode === 'SHARE' && (
          <span className="ml-1 tabular-nums">{pct(cell.targetShare)}</span>
        )}
      </Td>
      <Td className="text-right tabular-nums">
        {cell.target === null ? '—' : count(cell.target)}
      </Td>
      <Td>
        <span className="flex items-center gap-2">
          <span className="h-2 w-16 rounded bg-muted overflow-hidden shrink-0">
            {fill !== null && (
              <span className="block h-full bg-chocolate" style={{ width: `${fill * 100}%` }} />
            )}
          </span>
          <span className="tabular-nums text-erp-xs whitespace-nowrap">
            {count(cell.achieved)} / {cell.target === null ? '—' : count(cell.target)}
          </span>
        </span>
      </Td>
      <Td>
        {cell.openForCapture
          ? <span className="text-erp-xs text-muted-foreground">open</span>
          : <Pill tone="bg-amber-500/10 text-amber-600 border-amber-500/30">closed</Pill>}
      </Td>
    </tr>
  )
}
